<template>
  <div class="flex flex-col gap-5 h-full">
    <div
      v-if="!eventsStore.isEventLoaded"
      class="w-full h-full flex items-center justify-center p-8"
    >
      <WnLoader />
    </div>
    <div
      v-else-if="eventsStore.event"
      class="flex flex-col gap-5 text-gray-900"
    >
      <div
        v-if="!eventsStore.event?.config.general?.hideEventHeader"
        class="bg-white rounded-xl"
      >
        <EventsItemPageHeader />
      </div>
      <!-- <SubscriptionsBanner
        v-if="isShowSubscriptionBanner"
        :is-pro-active="authStore.isProActive"
      /> -->
      <PageEditor
        :page-link="eventLink"
        :is-admin="authStore.isAdmin"
        :event-id="eventsStore.event.id"
        :meta-title="eventsStore.event.name"
        :meta-description="eventsStore.event?.preview_description"
        :meta-image="logo || '/event-default-logo-1200x630.png'"
        :meta-keywords="`event, WN, ${eventsStore.event?.name}`"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useEvents } from '~/stores/events'
import { useAuth } from '~/stores/auth'
import { EventTypeRoutesMap, EventType } from '~/models/events'
import { useOrders } from '~/stores/orders'
import { removeMarkdown } from '~/utils/remove-markdown'
import type { Currency } from '~/models/orders'

definePageMeta({
  allowBack: true,
})

const nuxtApp = useNuxtApp()
const eventsStore = useEvents()
const { getEventPageRoute, getEventPageLink, fetchEvent } = eventsStore
const route = useRoute()
const authStore = useAuth()
const ordersStore = useOrders()
const { parseDate } = useDate()
const { getLocalMedia } = useLocalMedia()
const {
  $cityads: { trackProduct },
} = useNuxtApp()

const eventSlug = route.params.event as string

const eventLink = computed(() => {
  return getEventPageLink(getEventPageRoute(eventSlug, eventsStore.event?.type))
})

const isLoaded = ref(false)

const eventTitle = computed<string>(
  () => eventsStore.event?.tagline || eventsStore.event?.name || ''
)

// const isShowSubscriptionBanner = computed(() => {
//   return (
//     eventsStore.event &&
//     eventsStore.isEventAvailableForPro(eventsStore.event) &&
//     ordersStore.ticketsDetails.some((ticket) =>
//       ticket.features.some((feature) => feature.feature === 'pro_account')
//     )
//   )
// })

const logo = computed(() => {
  return getLocalMedia(eventsStore.event?.logo)?.url
})

const initGoogleEventsScript = () => {
  if (
    !eventsStore.event ||
    eventsStore.event.type !== EventType.Default ||
    !eventsStore.event.is_show
  ) {
    return
  }

  const getEventAttendanceMode = () => {
    if (eventsStore.event?.is_online && eventsStore.event?.is_onsite) {
      return 'https://schema.org/MixedEventAttendanceMode'
    } else if (eventsStore.event?.is_onsite) {
      return 'https://schema.org/OfflineEventAttendanceMode'
    } else {
      return 'https://schema.org/OnlineEventAttendanceMode'
    }
  }

  const offlineLocation = {
    '@type': 'Place',
    address:
      eventsStore.event.full_address ||
      eventsStore.event.address ||
      eventsStore.event.city ||
      '',
    url: eventsStore.event.address_link || '',
  }

  const onlineLocation = {
    '@type': 'VirtualLocation',
    url: eventLink.value,
  }

  const getLocation = () => {
    if (eventsStore.event?.is_online && eventsStore.event?.is_onsite) {
      return [offlineLocation, onlineLocation]
    } else if (eventsStore.event?.is_onsite) {
      return offlineLocation
    } else {
      return onlineLocation
    }
  }

  const formatDate = (date: string, timezone: string) => {
    const parsedDate = parseDate(date, timezone)

    return parsedDate ? parsedDate.format('YYYY-MM-DDTHH:mmZ') : ''
  }

  const validFromParsedDate = parseDate(
    eventsStore.event.date_from,
    eventsStore.event.timezone
  )

  const validFrom = validFromParsedDate
    ? validFromParsedDate.subtract(1, 'month').format('YYYY-MM-DDTHH:mm')
    : ''

  const currency = eventsStore.currency
  const offers = ordersStore.ticketsDetails.map((ticket) => ({
    '@type': 'Offer',
    url: eventsStore.event?.config?.login?.registrationUrl || eventLink.value,
    price: (ticket[currency.toLowerCase() as Lowercase<Currency>] || 0) / 100,
    priceCurrency: currency,
    category: ticket.name,
    availability: 'https://schema.org/InStock',
    validFrom,
  }))

  const fullDescription = eventsStore.event?.description
    ? removeMarkdown(eventsStore.event.description, {
        listUnicodeChar: '-',
      }).replaceAll('\n', ' ')
    : ''

  const googleEventsScript = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: eventTitle.value,
    startDate: formatDate(
      eventsStore.event.date_from,
      eventsStore.event.timezone
    ),
    endDate: formatDate(eventsStore.event.date_to, eventsStore.event.timezone),
    eventAttendanceMode: getEventAttendanceMode(),
    eventStatus: 'https://schema.org/EventScheduled',
    location: getLocation(),
    image: [logo.value || '/event-default-logo-1200x630.png'],
    description:
      fullDescription ||
      eventsStore.event.preview_description ||
      eventTitle.value,
    offers,
    performer: {
      '@type': 'PerformingGroup',
      name: 'WN Media Group',
    },
    organizer: {
      '@type': 'Organization',
      name: 'WN Media Group',
      url: 'https://wnhub.io/',
    },
  }

  useHead({
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify(googleEventsScript),
      },
    ],
  })
}

const initMeta = () => {
  useHead({
    link: [
      {
        rel: 'canonical',
        href: eventLink.value,
      },
    ],
  })
}

const load = async () => {
  isLoaded.value = false
  await fetchEvent(eventSlug, !!authStore.auth)
  await ordersStore.fetchTicketListDetails(eventsStore.event?.id)
  isLoaded.value = true

  if (!eventsStore.event) {
    return
  }

  if (
    process.client &&
    !route.fullPath.includes('/' + EventTypeRoutesMap[eventsStore.event.type])
  ) {
    await navigateTo(
      useLocaleRoute()(
        `/${EventTypeRoutesMap[eventsStore.event.type]}/${eventSlug}`
      )
    )
    return
  }

  nuxtApp.runWithContext(() => {
    initMeta()
    initGoogleEventsScript()
  })

  try {
    // TODO: uncomment for PRO banner
    // await fetchTicketListDetails(eventsStore.event.id)
  } catch (error: any) {
    useLogError(error)
  }
}

onMounted(async () => {
  await load()

  trackProduct(String(eventsStore.event?.id))
})

onServerPrefetch(async () => {
  await load()
})

// It seems that right now we don't have any elements that would depend on authorization
// For the event header, all needed requests are inside the component

// watch(
//   () => !!authStore.auth,
//   async (isAuth) => {
//     resetEvent()
//     try {
//       await fetchEvent(eventSlug, isAuth)
//     } catch (error: any) {
//       throw useCreateScreenError({ message: 'no such event' })
//     }
//   }
// )
</script>
